import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { MapPin, LocateFixed, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, fmtDateTime } from "@/lib/utils-format";

export default function MobileCheckin() {
  const [pos, setPos] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [recent, setRecent] = useState([]);

  const loadRecent = async () => {
    try {
      const { data } = await api.get("/mobile/checkins?scope=me");
      setRecent(data);
    } catch (e) {}
  };

  useEffect(() => {
    loadRecent();
    getLocation();
  }, []);

  const getLocation = () => {
    setError("");
    if (!navigator.geolocation) {
      setError("Geolocation is not supported in this browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setPos({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy });
      },
      (err) => setError(err.message || "Unable to fetch location. Please enable GPS."),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  const last = recent[0];
  const nextType = last?.type === "check_in" ? "check_out" : "check_in";

  const submit = async () => {
    if (!pos) {
      toast.error("Location required — enable GPS and retry.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/mobile/checkin", { type: nextType, lat: pos.lat, lng: pos.lng, accuracy: pos.accuracy, note });
      toast.success(`${nextType === "check_in" ? "Checked in" : "Checked out"} successfully`);
      setNote("");
      loadRecent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 space-y-4" data-testid="m-checkin-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#8B7355]">GPS Attendance</div>
        <h1 className="font-[Outfit] text-2xl font-semibold tracking-tight mt-0.5">
          {nextType === "check_in" ? "Check in" : "Check out"}
        </h1>
      </div>

      {/* Location card */}
      <div className="bg-white border border-[#EAE5D9] rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#C05621]" />
            <div className="font-semibold text-sm">Current location</div>
          </div>
          <button
            onClick={getLocation}
            data-testid="m-refresh-gps"
            className="text-xs text-[#C05621] hover:underline flex items-center gap-1"
          >
            <LocateFixed className="w-3 h-3" /> Refresh
          </button>
        </div>
        {error && (
          <div className="flex items-start gap-2 bg-red-50 text-red-700 rounded-md p-3 text-xs mb-2" data-testid="m-gps-error">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>{error}</div>
          </div>
        )}
        {pos ? (
          <div data-testid="m-gps-data">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#8B7355]">Latitude</div>
                <div className="font-mono">{pos.lat.toFixed(6)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#8B7355]">Longitude</div>
                <div className="font-mono">{pos.lng.toFixed(6)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#8B7355]">Accuracy</div>
                <div className="font-mono">±{Math.round(pos.accuracy || 0)}m</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#8B7355]">Map</div>
                <a
                  href={`https://www.google.com/maps?q=${pos.lat},${pos.lng}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#C05621] hover:underline text-xs"
                >
                  Open in Maps ↗
                </a>
              </div>
            </div>
          </div>
        ) : (
          !error && <div className="text-sm text-[#8B7355]">Getting your location…</div>
        )}
      </div>

      {/* Note */}
      <div>
        <label className="text-[11px] uppercase tracking-widest text-[#8B7355]">Note (optional)</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          data-testid="m-checkin-note"
          placeholder="e.g. Starting route from warehouse"
          className="w-full mt-1 rounded-md border border-[#EAE5D9] px-3 py-2 text-sm resize-none outline-none focus:border-[#C05621]"
        />
      </div>

      <button
        onClick={submit}
        disabled={busy || !pos}
        data-testid="m-checkin-submit"
        className={`w-full h-14 rounded-md text-white font-semibold uppercase tracking-widest text-sm disabled:opacity-50 ${
          nextType === "check_in" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"
        }`}
      >
        {busy ? "Recording…" : nextType === "check_in" ? "✓ Check in now" : "✓ Check out now"}
      </button>

      {/* Recent */}
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#8B7355] mb-2">Recent activity</div>
        <ul className="space-y-2">
          {recent.slice(0, 8).map((c) => (
            <li key={c.id} className="bg-white border border-[#EAE5D9] rounded-md p-3 flex items-center gap-3" data-testid={`m-checkin-item-${c.id}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${c.type === "check_in" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                <CheckCircle2 className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium capitalize">{c.type.replace("_", " ")}</div>
                <div className="text-xs text-[#8B7355]">{fmtDateTime(c.timestamp)}</div>
                {c.note && <div className="text-xs text-[#5C4033] italic mt-0.5 truncate">{c.note}</div>}
              </div>
              {c.lat && (
                <a
                  href={`https://www.google.com/maps?q=${c.lat},${c.lng}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#C05621] text-xs"
                >
                  <MapPin className="w-4 h-4" />
                </a>
              )}
            </li>
          ))}
          {recent.length === 0 && (
            <li className="text-center py-4 text-[#8B7355] text-sm">No check-ins yet.</li>
          )}
        </ul>
      </div>
    </div>
  );
}
